import { useRef, useState, useEffect } from 'react';
import React from 'react';
import './index.less';
import { useParams, redirect, useNavigate, useLocation } from 'react-router-dom';
import BottomNav from '../../components/BottomNav';
import { message, Card, Image, Descriptions, Button, Modal } from 'antd';

interface AddressInfo {
    hash: string;
    teamCard: {
        id: number;
        name: string;
        description: string;
        thumbnailUrl: string;
        englishName: string;
        teamName: string;
    };
    experience: number;
    level: number;
    ranking: number;
    militaryRank: string;
    lastCheckIn: string;
    contributionValue: number;
    customName: string;
    achievements: {
        id: number;
        achievementName: string;
        title: string;
        description: string;
    }[];
}

function Homepage(props: any) {
    const { hash } = useParams<{ hash: string }>();
    const [addressInfo, setAddressInfo] = useState<AddressInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [checkingIn, setCheckingIn] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showEditCustomNameModal, setShowEditCustomNameModal] = useState(false);
    const [newCustomName, setNewCustomName] = useState('');

    useEffect(() => {
        if (hash) {
            fetchAddressInfo(hash);
        }
    }, [hash]);

    const fetchAddressInfo = async (hash: string) => {
        try {
            setLoading(true);
            const response = await fetch(`/api/v1/backroomsteamcard/address/${hash}`);
            if (!response.ok) {
                throw new Error('获取地址信息失败');
            }
            const data = await response.json();
            setAddressInfo(data);
        } catch (error) {
            message.error('获取地址信息失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckIn = async () => {
        if (!hash) return;

        try {
            setCheckingIn(true);
            const response = await fetch(`/api/v1/backroomsteamcard/address/${hash}/check-in`, {
                method: 'POST'
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || '签到失败');
            }

            const result = await response.json();
            setAddressInfo(result.address);
            message.success(`签到成功！获得 ${result.addedExperience} 点经验值`);
        } catch (error: any) {
            message.error(error.message || '签到失败');
        } finally {
            setCheckingIn(false);
        }
    };

    const isTodayCheckedIn = () => {
        if (!addressInfo?.lastCheckIn) return false;
        const lastCheckIn = new Date(addressInfo.lastCheckIn);
        const today = new Date();
        return lastCheckIn.toDateString() === today.toDateString();
    };

    const calculateNextLevelExp = () => {
        if (addressInfo) {
            const nextLevel = addressInfo.level + 1;
            const baseExp = 160, expMultiplier = 1.08;
            const maxLevel = 100;

            // 边界处理：等级小于1或超过上限
            if (nextLevel <= 1) return 0;  // 1级无需经验
            const level = Math.min(nextLevel, maxLevel);  // 上限100级

            // 等比数列求和公式（计算1→2, 2→3, ..., (level-1)→level的经验总和）
            // 公式：S = baseExp * (expMultiplier^(level-1) - 1) / (expMultiplier - 1)
            const exponent = level - 1;  // 项数 = level-1（从1级到level级需要level-1次升级）
            const totalExp = baseExp * (Math.pow(expMultiplier, exponent) - 1) / (expMultiplier - 1);

            // 取整并返回（经验值为整数）
            return Math.round(totalExp);
        } else { return 0 }
    };

    const calculateProgress = () => {
        if (!addressInfo?.experience) return 0;
        const nextExp = calculateNextLevelExp();
        return Math.min(100, (addressInfo.experience / nextExp) * 100);
    };

    const handleUpdateCustomName = async () => {
        if (!hash) return;
        try {
            const response = await fetch(`/api/v1/backroomsteamcard/address/${hash}/custom-name`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ customName: newCustomName }),
            });

            if (!response.ok) {
                throw new Error('修改失败');
            }

            const data = await response.json();
            setAddressInfo(data);
            setShowEditCustomNameModal(false);
            message.success('修改成功');
        } catch (error: any) {
            message.error(error.message || '修改失败');
        }
    };

    return (
        <div id="homePage">

            <div className="basicinfo">
                <div className="preview">
                    <img src={addressInfo?.teamCard.thumbnailUrl} alt={addressInfo?.teamCard.name} />
                </div>
                <div className="info">
                    <div className="level">
                        <div className="level-text">等级{addressInfo?.level}</div>
                        <div className="tip" onClick={() => setShowUpgradeModal(true)}>如何升级？</div>
                    </div>

                    <div className="experience">
                        <div className="progress-bar">
                            <div
                                className="progress"
                                style={{
                                    width: `${calculateProgress()}%`,
                                    backgroundColor: 'var(--primary-color)'
                                }}
                            />
                        </div>
                        <div className="experience-info">
                            <span>{addressInfo?.experience || 0}</span>
                            <span>/</span>
                            <span>{calculateNextLevelExp()}</span>
                        </div>
                    </div>

                    <div className="stats">
                        <div className="stat-item">
                            <div className="stat-label">卡牌战斗分</div>
                            <div className="stat-value">{addressInfo?.contributionValue || 0}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-label">拥有成就</div>
                            <div className="stat-value">{addressInfo?.achievements?.length || 0}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-label">官衔(实时)</div>
                            <div className="stat-value">{addressInfo?.militaryRank || '-'}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-label">排名(实时)</div>
                            <div className="stat-value">No.{addressInfo?.ranking || '-'}</div>
                        </div>
                    </div>

                    <div className="link">
                        <a href={`/ranking`}>点击查看全服排名</a>
                    </div>


                </div>
            </div>

            <div className="cardDetail">
                <div className="stats">
                    <div className="stat-item">
                        <div className="stat-label">专属名称</div>
                        <div className="stat-value customname">{addressInfo?.customName}<span className="edit" onClick={() => setShowEditCustomNameModal(true)}>(点击修改)</span></div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-label">名称</div>
                        <div className="stat-value name">{addressInfo?.teamCard.teamName}</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-label">英文名</div>
                        <div className="stat-value">{addressInfo?.teamCard.englishName}</div>
                    </div>

                    <div className="stat-item">
                        <div className="stat-label">描述</div>
                        <div className="stat-value">{addressInfo?.teamCard.description}</div>
                    </div>
                </div>

            </div>

            <div className="buttons">

                <div className={`checkin ${isTodayCheckedIn() ? 'checked' : ''}`}
                    onClick={handleCheckIn}
                >
                    {isTodayCheckedIn() ? '今日已签到' : '签到'}
                </div>

                <div className="linktocards">
                    <a href={`/cards/${hash}`}>点此查看所有卡片图鉴</a>
                </div>

            </div>

            <Modal
                title="如何升级"
                open={showUpgradeModal}
                onCancel={() => setShowUpgradeModal(false)}
                footer={null}
            >
                <div className="upgrade-methods">
                    <p>方法1：</p>
                    <p>签到：每日增加经验值</p>
                    <p>方法2：</p>
                    <p>参加事件，若事件成功完成获取经验值及成就</p>
                </div>
            </Modal>

            <Modal
                title="修改专属名称"
                open={showEditCustomNameModal}
                onOk={handleUpdateCustomName}
                onCancel={() => setShowEditCustomNameModal(false)}
            >
                <input
                    type="text"
                    value={newCustomName}
                    onChange={(e) => setNewCustomName(e.target.value)}
                    placeholder="请输入新的专属名称"
                />
            </Modal>

            <BottomNav hash={hash} />
        </div>
    );
}

export default Homepage;